// ── Episode type classifier ────────────────────────────────────────────────────
// Types: "live-group" | "live-solo" | "interview" | "episode"
// Three-layer system:
//   1. Rule-based scoring (fast, runs on every video)
//   2. AI classification via Claude (for low-confidence videos)
//   3. Manual override (always wins)

// ── Signal libraries ──────────────────────────────────────────────────────────

const LIVE_TITLE_TERMS = [
  "live stream", "livestream", "live show", "live episode", "live event",
  "watching live", "going live", " live ", "live q&a", "live chat",
  "live reaction", "live discussion", "live breakdown", "live review",
  "stream live", "live premiere", "live tonight", "live now",
  "#live", "!live",
];

const LIVE_DESC_TERMS = [
  "join us live", "streaming live", "tune in live", "live every",
  "live stream", "we go live", "i go live", "catch us live",
  "super chat", "superchat", "join the stream", "watch live",
  "powered by streamyard", "streamyard", "restream", "stream yard",
  "chat with us live", "join us on youtube live", "live on youtube",
  "stream starts", "go live at", "join me live",
];

const LIVE_TRANSCRIPT_TERMS = [
  "welcome to the stream", "welcome back to the stream",
  "we are live", "we're live", "just went live",
  "chat is going crazy", "in the chat", "the chat is saying",
  "super chat", "read that super chat",
  "shoutout to", "welcome to the show everyone",
  "hey everyone we're live", "thanks for joining the stream",
  "smash that like button while we're live",
];

// StreamYard and multi-streaming platform signals
const STREAMYARD_DESC_TERMS = [
  "streamyard", "stream yard", "restream.io", "restream",
  "this stream was recorded", "recorded live", "originally aired live",
  "live recording", "stream recording", "live episode recorded",
  "aired live on", "broadcast live", "live broadcast",
];

const GROUP_TITLE_TERMS = [
  " w/ ", " w/", "feat.", "ft.", "featuring",
  "joins", "joined by", "vs ", "vs. ",
  "panel", "roundtable", "squad", "crew", "collab",
  "& the ", "and the boys", "and the girls",
  "the gang", "the team", "the crew",
];

// Guest patterns — "Title: First Last" or "with First Last" at end of title
// These catch interview-style titles without the word "interview"
const GUEST_NAME_PATTERNS = [
  /:\s+[A-Z][a-z]+ [A-Z][a-z]+/,           // "Topic: John Smith"
  /with [A-Z][a-z]+ [A-Z][a-z]+/,           // "with John Smith"
  /ft\. [A-Z][a-z]+ [A-Z][a-z]+/,           // "ft. John Smith"
  /w\/ [A-Z][a-z]+ [A-Z][a-z]+/,            // "w/ John Smith"
  /featuring [A-Z][a-z]+ [A-Z][a-z]+/i,     // "featuring John Smith"
  /\| [A-Z][a-z]+ [A-Z][a-z]+$/,            // "Topic | John Smith" at end
  /— [A-Z][a-z]+ [A-Z][a-z]+$/,             // "Topic — John Smith" at end
];

const INTERVIEW_TITLE_TERMS = [
  "interview", "interviews", "interviewed", "interviewing",
  "sits down with", "talks with", "talks to", "speaks with",
  "conversation with", "chat with", "q&a with", "qa with",
  "special guest", "asks", "reacts to", "responds to",
  "one on one", "1 on 1", "exclusive with",
  "breaks down", "explains", "reveals", "exposes",
  "confesses", "admits", "opens up",
];

const INTERVIEW_DESC_TERMS = [
  "today i sit down", "my guest", "joining me today", "today's guest",
  "special guest", "i interview", "we interview", "our guest",
  "in this interview", "for this interview", "sat down with",
  "joined by", "welcome to the show", "please welcome",
  "this week's guest", "this episode's guest",
  "exclusive interview", "first interview", "never before seen",
];

const INTERVIEW_TRANSCRIPT_TERMS = [
  "welcome to the show", "thanks for having me", "thank you for having me",
  "glad to be here", "happy to be here", "introduce yourself",
  "tell us about yourself", "tell me about yourself",
  "joining me today", "my guest today", "i'd like to welcome",
  "let's welcome", "please welcome", "excited to have you",
  "thanks for coming on", "thanks for joining us",
  "you've been doing this for", "tell me how you got into",
];

// Patterns that strongly suggest solo content
const SOLO_TITLE_TERMS = [
  "my take", "my thoughts", "my reaction", "my review",
  "i tried", "i tested", "i spent", "i visited",
  "solo", "alone", "by myself", "just me",
];

// ── Scoring engine ─────────────────────────────────────────────────────────────

function scoreTerms(text, terms) {
  if (!text) return 0;
  const lower = text.toLowerCase();
  return terms.filter(t => lower.includes(t)).length;
}

function scorePatterns(text, patterns) {
  if (!text) return 0;
  return patterns.filter(p => p.test(text)).length;
}


// Episode number patterns — very strong episode signal
const EPISODE_NUMBER_PATTERNS = [
  /\bep\.?\s*\d+/i,          // ep. 123, ep123
  /\bepisode\s*\d+/i,         // episode 123
  /\b#\d{2,}/,                 // #123
  /\bvol\.?\s*\d+/i,         // vol. 12
  /\bpart\s*\d+/i,            // part 2
  /\b\d{3,4}\b/,              // standalone 3-4 digit number (common in podcast titles)
];

export function classifyVideo(video, transcript = "", playlistMembership = [], schema = null) {
  const title = video.title || "";
  const titleLow = title.toLowerCase();
  const desc = (video.description || "").slice(0, 1000);
  const descLow = desc.toLowerCase();
  const tx = (transcript || "").slice(0, 3000).toLowerCase();
  const scores = { live: 0, group: 0, interview: 0, solo: 0, episode: 30 };
  const signals = [];

  // ── LIVE DETECTION ─────────────────────────────────────────────────────────

  // ── EPISODE NUMBER PATTERNS (very strong episode signal) ─────────────────
  const epNumberHits = EPISODE_NUMBER_PATTERNS.filter(p => p.test(title)).length;
  if (epNumberHits > 0) {
    scores.episode += epNumberHits * 35;
    scores.interview -= epNumberHits * 20; // numbered episodes are rarely interviews
    signals.push("episode number in title");
  }

  // ── PLAYLIST SIGNALS (very high confidence) ───────────────────────────────
  if (playlistMembership.length > 0) {
    const playlistTitles = playlistMembership.map(p => p.title.toLowerCase());

    if (schema) {
      // Use channel-specific schema hints
      schema.forEach(typeConfig => {
        const hints = typeConfig.playlistHints || [];
        const inPlaylist = playlistTitles.some(t => hints.some(h => t.includes(h)));
        if (inPlaylist) {
          if (!scores[typeConfig.id]) scores[typeConfig.id] = 0;
          scores[typeConfig.id] += 65;
          signals.push(`${typeConfig.label} playlist`);
        }
      });
    } else {
      // Default playlist matching
      const inLivePlaylist = playlistTitles.some(t =>
        /live|stream|streams|livestream/.test(t) && !/playlist|videos|all/.test(t)
      );
      const inInterviewPlaylist = playlistTitles.some(t =>
        /interview|interviews|guest|guests|conversations|talks/.test(t)
      );
      const inEpisodePlaylist = playlistTitles.some(t =>
        /episode|episodes|podcast|show|series/.test(t) && !/live|stream/.test(t)
      );
      if (inLivePlaylist) { scores.live += 70; signals.push("live playlist"); }
      if (inInterviewPlaylist) { scores.interview += 60; signals.push("interview playlist"); }
      if (inEpisodePlaylist) { scores.episode += 40; signals.push("episode playlist"); }
    }
  }

  // ── LIVE STREAMING METADATA (definitive) ──────────────────────────────────
  if (video.actualStartTime) {
    scores.live += 95;
    signals.push("actualStartTime present");
  }
  if (video.scheduledStartTime && !video.actualStartTime) {
    scores.live += 30; // scheduled but may not have aired yet
    signals.push("scheduled live");
  }

  // YouTube native live flag (strongest signal)
  if (video.liveBroadcastContent === "completed" || video.wasLive) {
    scores.live += 90;
    signals.push("YouTube live flag");
  }

  // ── DURATION SIGNALS ──────────────────────────────────────────────────────
  const duration = video.durationSeconds || 0;
  if (duration > 0) {
    if (duration <= 60) {
      scores.episode -= 20; // shorts are rarely full episodes
      signals.push("short duration (<60s)");
    } else if (duration < 300) {
      // 1-5 min: likely a clip or short episode, not live
      scores.live -= 15;
      signals.push("clip-length duration (1-5min)");
    } else if (duration >= 3600) {
      // 60+ min: strongly suggests live stream or long-form episode
      scores.live += 20;
      signals.push("long duration (60min+)");
    } else if (duration >= 7200) {
      // 2hr+: almost certainly a live stream
      scores.live += 35;
      signals.push("very long duration (2hr+)");
    }
  }

  // ── TAG SIGNALS ───────────────────────────────────────────────────────────
  const tags = (video.tags || []).map(t => t.toLowerCase());
  if (tags.length > 0) {
    const liveTagTerms = ["live", "livestream", "live stream", "live show", "stream"];
    const interviewTagTerms = ["interview", "guest", "conversation", "talk", "podcast guest"];
    const episodeTagTerms = ["podcast", "episode", "ep.", "#", "show"];

    if (tags.some(t => liveTagTerms.includes(t))) { scores.live += 25; signals.push("live tag"); }
    if (tags.some(t => interviewTagTerms.some(it => t.includes(it)))) { scores.interview += 25; signals.push("interview tag"); }
    if (tags.some(t => episodeTagTerms.some(et => t.includes(et)))) { scores.episode += 15; signals.push("podcast/episode tag"); }
  }

  // ── YOUTUBE CATEGORY ──────────────────────────────────────────────────────
  // Category 22 = People & Blogs (common for podcasts/interviews)
  // Category 24 = Entertainment
  // Category 28 = Science & Technology
  if (video.categoryId === "22") { scores.episode += 10; signals.push("People & Blogs category"); }

  // ── ENGAGEMENT SIGNALS ────────────────────────────────────────────────────
  // Very low like ratio on high-view video suggests live stream (live viewers don't like as much)
  if (video.viewCount > 1000 && video.likeCount > 0) {
    const likeRatio = video.likeCount / video.viewCount;
    if (likeRatio < 0.01 && duration > 1800) {
      scores.live += 15;
      signals.push("low like ratio (live pattern)");
    }
  }

  // StreamYard / multi-streaming platform
  const streamyardHits = scoreTerms(descLow, STREAMYARD_DESC_TERMS);
  if (streamyardHits > 0) {
    scores.live += streamyardHits * 40;
    signals.push(`streaming platform detected (${streamyardHits})`);
  }

  // Live title keywords
  const liveTitleHits = scoreTerms(titleLow, LIVE_TITLE_TERMS);
  if (liveTitleHits > 0) {
    scores.live += liveTitleHits * 35;
    signals.push(`live title keywords (${liveTitleHits})`);
  }

  // Live description keywords
  const liveDescHits = scoreTerms(descLow, LIVE_DESC_TERMS);
  if (liveDescHits > 0) {
    scores.live += liveDescHits * 20;
    signals.push(`live desc keywords (${liveDescHits})`);
  }

  // Live transcript cues
  const liveTxHits = scoreTerms(tx, LIVE_TRANSCRIPT_TERMS);
  if (liveTxHits > 0) {
    scores.live += liveTxHits * 25;
    signals.push(`live transcript cues (${liveTxHits})`);
  }

  // Duration heuristics
  if (duration > 7200) { scores.live += 30; signals.push("2hr+ duration"); }
  else if (duration > 5400) { scores.live += 15; signals.push("90min+ duration"); }

  // ── GROUP / MULTI-PERSON ───────────────────────────────────────────────────

  const groupTitleHits = scoreTerms(titleLow, GROUP_TITLE_TERMS);
  if (groupTitleHits > 0) {
    scores.group += groupTitleHits * 30;
    signals.push(`group title keywords (${groupTitleHits})`);
  }

  const guestNameHits = scorePatterns(title, GUEST_NAME_PATTERNS);
  if (guestNameHits > 0) {
    scores.group += guestNameHits * 15; // reduced — guest in title ≠ interview format
    scores.interview += guestNameHits * 8; // very low — guest name alone doesn't make it an interview
    signals.push(`guest name in title`);
  }

  // ── INTERVIEW DETECTION ────────────────────────────────────────────────────

  const intTitleHits = scoreTerms(titleLow, INTERVIEW_TITLE_TERMS);
  if (intTitleHits > 0) {
    scores.interview += intTitleHits * 40;
    signals.push(`interview title keywords (${intTitleHits})`);
  }

  const intDescHits = scoreTerms(descLow, INTERVIEW_DESC_TERMS);
  if (intDescHits > 0) {
    scores.interview += intDescHits * 25;
    signals.push(`interview desc cues (${intDescHits})`);
  }

  const intTxHits = scoreTerms(tx, INTERVIEW_TRANSCRIPT_TERMS);
  if (intTxHits > 0) {
    scores.interview += intTxHits * 30;
    signals.push(`interview transcript cues (${intTxHits})`);
  }

  // Mid-duration range favors interview (20-90min)
  if (duration >= 1200 && duration <= 5400) {
    scores.interview += 10;
  }

  // ── SOLO DETECTION ─────────────────────────────────────────────────────────

  const soloHits = scoreTerms(titleLow, SOLO_TITLE_TERMS);
  if (soloHits > 0) {
    scores.solo += soloHits * 20;
    signals.push(`solo title keywords`);
  }

  // ── RESOLVE TYPE ───────────────────────────────────────────────────────────

  // ── RESOLVE TYPE ───────────────────────────────────────────────────────────

  // LIVE wins definitively if score is high enough
  if (scores.live >= 45) {
    const isGroup = scores.group >= 20 || guestNameHits > 0;
    const type = isGroup ? "live-group" : "live-solo";
    const confidence = Math.min(100, Math.round(
      scores.live * 0.7 + (isGroup ? scores.group * 0.3 : scores.solo * 0.1)
    ));
    return { type, confidence, signals, scores, needsAI: confidence < 65 };
  }

  // INTERVIEW wins only if it has strong dedicated interview signals
  // NOT just a guest name in title — podcasts routinely have guests but are still "episodes"
  // True interview signals: explicit "interview" keyword, dedicated interview playlist,
  // interview-specific title patterns beyond just a guest name
  const hasStrongInterviewSignal = (
    titleLow.includes("interview") ||
    titleLow.includes("sits down") ||
    titleLow.includes("talks to") ||
    titleLow.includes("speaks with") ||
    descLow.includes("exclusive interview") ||
    signals.includes("interview playlist")
  );
  const interviewThreshold = hasStrongInterviewSignal ? 35 : 70;

  if (scores.interview > scores.episode && scores.interview >= interviewThreshold) {
    const confidence = Math.min(100, Math.round(scores.interview));
    return { type: "interview", confidence, signals, scores, needsAI: confidence < 65 };
  }

  // Check custom schema types — find highest scoring custom type
  if (schema) {
    let bestCustomType = null;
    let bestCustomScore = 55; // minimum threshold
    schema.forEach(typeConfig => {
      const s = scores[typeConfig.id] || 0;
      if (s > bestCustomScore && !["episode","live-solo","live-group","interview"].includes(typeConfig.id)) {
        bestCustomScore = s;
        bestCustomType = typeConfig;
      }
    });
    if (bestCustomType) {
      return {
        type: bestCustomType.id,
        confidence: Math.min(100, bestCustomScore),
        signals,
        scores,
        needsAI: bestCustomScore < 65,
      };
    }
  }

  // EPISODE is the default
  const epConfidence = Math.max(40, Math.round(
    100 - Math.max(scores.live * 0.4, scores.interview * 0.3, scores.group * 0.2)
  ));
  return { type: "episode", confidence: epConfidence, signals, scores, needsAI: epConfidence < 40 };
}

// ── AI classification via Claude ───────────────────────────────────────────────
// Called for videos where confidence < threshold
// Returns same shape as classifyVideo()

// callFn is passed in from the caller (ClassifyReview or PageComponents)
// to avoid a circular import: classifier → claudeClient → firebase → classifier
export async function classifyVideoWithAI(video, transcript = "", callFn = null) {
  const prompt = `Classify this YouTube video into exactly one of these four types:
- episode: a regular solo podcast/show episode
- live-solo: a live stream with just the host(s), no outside guests
- live-group: a live stream with external guests or multiple participants joining remotely or in person  
- interview: a recorded (non-live) conversation or interview with one or more guests

Video title: "${video.title}"
Duration: ${Math.round((video.durationSeconds||0)/60)} minutes
Description (first 400 chars): "${(video.description||"").slice(0,400)}"
${transcript ? `Transcript excerpt (first 300 words): "${transcript.slice(0,1500)}"` : ""}

Reply with ONLY valid JSON, no markdown:
{"type":"episode","confidence":85,"reason":"brief one-sentence reason"}

Type must be exactly one of: episode, live-solo, live-group, interview
Confidence is 0-100.`;

  try {
    if (!callFn) throw new Error("No AI call function provided");
    const parsed = await callFn(prompt, 120);
    if (!["episode","live-solo","live-group","interview"].includes(parsed.type)) {
      throw new Error("Invalid type returned");
    }
    return {
      type: parsed.type,
      confidence: Math.min(100, Math.max(0, parseInt(parsed.confidence) || 70)),
      signals: ["AI classification: " + (parsed.reason || "")],
      scores: {},
      aiClassified: true,
      needsAI: false,
    };
  } catch {
    return null; // Fall back to rule-based result
  }
}

// ── Type metadata ──────────────────────────────────────────────────────────────
export const TYPE_META = {
  "episode":    { label:"Episode",    short:"EP",    color:"#2B5EA7", bg:"rgba(43,94,167,0.15)" },
  "live-solo":  { label:"Live Solo",  short:"LIVE",  color:"#D93025", bg:"rgba(217,48,37,0.15)" },
  "live-group": { label:"Live Group", short:"LIVE+", color:"#C9A227", bg:"rgba(201,162,39,0.15)" },
  "interview":  { label:"Interview",  short:"INTV",  color:"#16a34a", bg:"rgba(22,163,74,0.15)" },
};

export const ALL_TYPES = Object.keys(TYPE_META);

// ── Sanitizers ─────────────────────────────────────────────────────────────────

export function sanitizeCommentHtml(html) {
  if (!html) return "";
  return html
    .replace(/<(?!\/?(?:b|i|em|strong|br|a)\b)[^>]+>/gi, "")
    .replace(/\s+on\w+="[^"]*"/gi, "")
    .replace(/\s+on\w+='[^']*'/gi, "")
    .replace(/href="javascript:[^"]*"/gi, 'href="#"')
    .replace(/href='javascript:[^']*'/gi, "href='#'")
    .slice(0, 2000);
}

export function validateApiKey(key) {
  if (!key || typeof key !== "string") return { valid: false, error: "API key is required." };
  const trimmed = key.trim();
  if (!trimmed.startsWith("AIza")) return { valid: false, error: "Invalid API key format. YouTube API keys start with AIza." };
  if (trimmed.length < 35 || trimmed.length > 45) return { valid: false, error: "API key length looks wrong. Check you copied the full key." };
  return { valid: true, value: trimmed };
}

export function validateChannelInput(input) {
  if (!input || typeof input !== "string") return { valid: false, error: "Channel ID or @handle is required." };
  const trimmed = input.trim();
  if (trimmed.startsWith("@")) {
    if (!/^@[\w.-]{3,}$/.test(trimmed)) return { valid: false, error: "Invalid @handle format." };
    return { valid: true, value: trimmed };
  }
  if (trimmed.startsWith("UC")) {
    if (trimmed.length < 20 || trimmed.length > 30) return { valid: false, error: "Channel ID looks wrong — should start with UC and be about 24 characters." };
    return { valid: true, value: trimmed };
  }
  if (/^[\w.-]{3,}$/.test(trimmed)) return { valid: true, value: trimmed };
  return { valid: false, error: "Enter a @handle, channel ID (UC...), or channel username." };
}

export function sanitizeText(text, maxLen = 500) {
  if (!text || typeof text !== "string") return "";
  return text.replace(/[<>]/g, "").slice(0, maxLen);
}
